/**
 * src/components/Financial/modules/MoonPhaseModule.jsx
 * 【完全修正版：全期間蝕検索・Q/C削除・ダークモード対応】
 */
import React from "react";
import { Moon, Info } from "lucide-react";
import * as Astro from "../../../astronomy.js";

/**
 * 月相・蝕の計算ロジック
 * MoonStateSection と同様の Astro 関数を使用し、タイムライン全域を検索します。
 */
export const calculateMoonEvents = function (timeline) {
  var hits = [];
  if (!timeline || timeline.length < 2) return hits;

  var getDate = function (row) {
    return row.timestamp ? new Date(row.timestamp) : new Date(row.Date);
  };

  var startDate = getDate(timeline[0]);
  var endDate = getDate(timeline[timeline.length - 1]);

  // --- 1. 新月・満月の検出 (角度交差判定で漏れを防止) ---
  var targets = [
    { angle: 0, label: "新月", type: "major", symbol: "N", color: "#475569" },
    { angle: 180, label: "満月", type: "major", symbol: "F", color: "#f59e0b" },
  ];

  var getMoonPhaseAngle = function (date) {
    var mLon = Astro.getPlanetLongitude("Moon", date);
    var sLon = Astro.getPlanetLongitude("Sun", date);
    if (mLon === null || sLon === null) return null;
    return (mLon - sLon + 360) % 360;
  };

  var checkCross = function (v1, v2, target) {
    var d1 = ((v1 - target + 540) % 360) - 180;
    var d2 = ((v2 - target + 540) % 360) - 180;
    return (
      Math.abs(d1) < 90 &&
      Math.abs(d2) < 90 &&
      ((d1 <= 0 && d2 > 0) || (d1 >= 0 && d2 < 0))
    );
  };

  for (var i = 1; i < timeline.length; i++) {
    var p1 = getMoonPhaseAngle(getDate(timeline[i - 1]));
    var p2 = getMoonPhaseAngle(getDate(timeline[i]));
    if (p1 === null || p2 === null) continue;

    targets.forEach(function (t) {
      if (checkCross(p1, p2, t.angle)) {
        hits.push({
          index: i,
          date:
            timeline[i].Date ||
            getDate(timeline[i]).toISOString().split("T")[0],
          label: t.label,
          type: t.type,
          symbol: t.symbol,
          color: t.color,
        });
      }
    });
  }

  // --- 2. 日食・月食の検出 (MoonStateSection の Astro.getCelestialEvents を応用) ---
  try {
    // タイムラインを約3ヶ月（90日）ごとのステップでスキャンして蝕を検索
    var stepMs = 90 * 86400000;
    for (var ts = startDate.getTime(); ts <= endDate.getTime(); ts += stepMs) {
      var scanDate = new Date(ts);
      var events = Astro.getCelestialEvents(scanDate, {
        isHeliocentric: false,
        isSidereal: false,
      });

      var eclipses = events.eclipses || [];
      eclipses.forEach(function (ecl) {
        if (!ecl.date || ecl.date > endDate || ecl.date < startDate) return;

        // イベント時刻に最も近いローソク足を探す
        var eventTs = ecl.date.getTime();
        var closestIdx = -1;
        var minDiff = Infinity;

        for (var j = 0; j < timeline.length; j++) {
          var diff = Math.abs(getDate(timeline[j]).getTime() - eventTs);
          if (diff < minDiff) {
            minDiff = diff;
            closestIdx = j;
          }
        }

        // 重複登録を避けて追加
        var isDuplicate = hits.some(
          (h) => h.label === ecl.type && h.index === closestIdx,
        );
        if (closestIdx !== -1 && minDiff < 86400000 * 1.5 && !isDuplicate) {
          hits.push({
            index: closestIdx,
            date: ecl.date.toISOString().split("T")[0],
            label: ecl.type,
            type: "eclipse",
            symbol: ecl.symbol,
            color: ecl.type.indexOf("Solar") !== -1 ? "#ef4444" : "#8b5cf6",
          });
        }
      });
    }
  } catch (e) {
    console.warn("Eclipse calculation error:", e);
  }

  return hits;
};

/**
 * 月相（ルネーション）描画レイヤー
 */
export const MoonPhaseLayer = function (props) {
  var { show, lunations, config, coords, setHoverLunation, isDarkMode } = props;
  if (!show) return null;

  var filtered = (lunations || []).filter(function (lv) {
    if (lv.type === "major") return config.major;
    if (lv.type === "eclipse") return config.eclipse;
    return false;
  });

  return (
    <g className="moon-phase-layer">
      {filtered.map(function (lv, i) {
        var xPos = coords.getX(lv.index - coords.effectiveStart);
        var yPos = coords.pad.t + coords.iH - 15;
        return (
          <g
            key={"lu-" + i}
            style={{ cursor: "pointer" }}
            onMouseEnter={() => setHoverLunation(lv)}
            onMouseLeave={() => setHoverLunation(null)}
          >
            <circle cx={xPos} cy={yPos} r="12" fill="transparent" />
            <circle
              cx={xPos}
              cy={yPos}
              r="8"
              fill={lv.color}
              opacity="0.9"
              stroke={isDarkMode ? "rgba(255, 255, 255, 0.4)" : "#ffffff"}
              strokeWidth="1"
            />
            <text
              x={xPos}
              y={yPos + 4}
              fill="#fff"
              fontSize="9"
              textAnchor="middle"
              style={{ pointerEvents: "none", fontWeight: "bold" }}
            >
              {lv.symbol}
            </text>
          </g>
        );
      })}
    </g>
  );
};

/**
 * 月相設定UI (Q/C削除済)
 */
export const MoonPhaseSettingsUI = function (props) {
  var { show, setShow, config, setConfig, isDarkMode } = props;
  var theme = {
    cardBg: isDarkMode ? "#1c1f26" : "#f8fafc",
    border: isDarkMode ? "#2d313d" : "#e2e8f0",
    text: isDarkMode ? "#ffffff" : "#1e293b",
  };

  var btnSt = (active) => ({
    padding: "6px 20px",
    fontSize: "11px",
    fontWeight: "bold",
    borderRadius: "4px",
    cursor: "pointer",
    background: active ? "#2c2d96" : isDarkMode ? "#2d2c33" : "#ffffff",
    color: active ? "#fff" : isDarkMode ? "#ccc" : "#64748b",
    border:
      "1px solid " +
      (active ? "#2c2d96" : isDarkMode ? "rgba(255,255,255,0.1)" : "#cbd5e1"),
  });

  return (
    <div
      style={{
        background: theme.cardBg,
        padding: "20px",
        borderRadius: "12px",
        border: "1px solid " + theme.border,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "15px",
        }}
      >
        <div
          style={{
            fontSize: "15px",
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            color: theme.text,
          }}
        >
          <Moon size={18} color={isDarkMode ? "#ffffff" : "#2c2d96"} />
          <span>ルネーション (月相・蝕)</span>
        </div>
        <button onClick={() => setShow(!show)} style={btnSt(show)}>
          {show ? "ON" : "OFF"}
        </button>
      </div>
      <div
        style={{
          display: "flex",
          gap: "20px",
          borderTop:
            "1px solid " + (isDarkMode ? "rgba(255,255,255,0.05)" : "#e2e8f0"),
          paddingTop: "15px",
          opacity: show ? 1 : 0.5,
          pointerEvents: show ? "auto" : "none",
        }}
      >
        <label
          style={{
            fontSize: "12px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            cursor: "pointer",
            color: theme.text,
          }}
        >
          <input
            type="checkbox"
            checked={config.major}
            onChange={() =>
              setConfig(Object.assign({}, config, { major: !config.major }))
            }
            style={{ colorScheme: isDarkMode ? "dark" : "light" }}
          />
          <span>満月・新月 (N/F)</span>
        </label>
        <label
          style={{
            fontSize: "12px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            cursor: "pointer",
            color: theme.text,
          }}
        >
          <input
            type="checkbox"
            checked={config.eclipse}
            onChange={() =>
              setConfig(Object.assign({}, config, { eclipse: !config.eclipse }))
            }
            style={{ colorScheme: isDarkMode ? "dark" : "light" }}
          />
          <span>日食・月食 (SE/LE)</span>
        </label>
      </div>
    </div>
  );
};
